import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Button,
    Chip,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Avatar,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Tooltip,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import MobileContainer from "../global/components/MobileContainer";
import RoundedSearchBar from "../global/components/RoundedSearchBar";
import ShoppingListDrawer from "../global/components/ShoppingListDrawer";
import { ProductsLoader } from "../components/ProductsLoader";
import BarcodeScanner from "../components/BarcodeScanner";
import { useProducts } from "../hooks/useProducts";
import { useShoppingListRedux } from "../hooks/useShoppingListRedux";
import { MAIN_COLOR_RED } from "../assets/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { RawProduct } from "../types";
import { shoppingListApi } from "../api";

interface SavedShoppingList {
    id: string;
    name: string;
    items: Record<string, number>;
    createdAt: string; // ISO string
    updatedAt: string; // ISO string
}

const ShoppingCartPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [favorites, setFavorites] = useState<Set<string>>(new Set());
    const [showFavorites, setShowFavorites] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [listName, setListName] = useState("");
    const [editingListId, setEditingListId] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const productsPerPage = 12;
    const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
    const [scannedProduct, setScannedProduct] = useState<RawProduct | null>(null);
    const [showCameraScanner, setShowCameraScanner] = useState(false);
    const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const {
        shoppingList,
        savedLists,
        addItem,
        removeItem,
        removeItemFromList,
        clearCurrentList,
        saveList,
        loadList,
        deleteList,
        editList
    } = useShoppingListRedux();

    const { 
        products, 
        getCategories
    } = useProducts();

    const categories = getCategories();

    const filteredProducts = products.filter((product: RawProduct) => {
        const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory ? product.category === selectedCategory : true;
        const matchesFavorites = showFavorites ? favorites.has(product.unique_id) : true;
        return matchesSearch && matchesCategory && matchesFavorites;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
    const startIndex = (currentPage - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    const currentProducts = filteredProducts.slice(startIndex, endIndex);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCategory, showFavorites]);

    const selectedProducts = products
        .filter((p: RawProduct) => shoppingList[p.unique_id])
        .map((p: RawProduct) => ({
            id: parseInt(p.unique_id),
            title: p.title,
            description: p.shortDescription,
            price: parseFloat(p.sale_price) || 0,
            quantity: shoppingList[p.unique_id],
            image: p.image_url,
            discount: p.promo_percentage && parseFloat(p.promo_percentage) > 0 ? `${p.promo_percentage}% OFF` : null,
        }));

    const totalPrice = selectedProducts.reduce((sum: number, p) => {
        return sum + (p.price * p.quantity);
    }, 0);

    const totalDiscount = selectedProducts.reduce((sum: number, p) => {
        const product = products.find(prod => prod.unique_id === p.id.toString());
        if (product && product.barred_price && parseFloat(product.barred_price) > parseFloat(product.sale_price)) {
            const originalPrice = parseFloat(product.barred_price);
            const salePrice = parseFloat(product.sale_price);
            const discountPerUnit = originalPrice - salePrice;
            return sum + (discountPerUnit * p.quantity);
        }
        return sum;
    }, 0);



    const toggleFavorite = (id: string) => {
        setFavorites((prev) => {
            const newFavorites = new Set(prev);
            if (newFavorites.has(id)) {
                newFavorites.delete(id);
            } else {
                newFavorites.add(id);
            }
            return newFavorites;
        });
    };

    const toggleFavoritesView = () => {
        setShowFavorites(!showFavorites);
    };

    // Check if this is the first time user
    useEffect(() => {
        const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
        if (!hasSeenWelcome) {
            setWelcomeDialogOpen(true);
        }
    }, []);

    // Handle navigation state for loading lists from routing page
    useEffect(() => {
        if (location.state?.loadList) {
            const listToLoad = location.state.loadList;
            loadList(listToLoad);
            // Clear the navigation state
            navigate(location.pathname, { replace: true });
        }
    }, [location.state, navigate, location.pathname, loadList]);

    const handleSaveList = () => {
        if (!listName.trim()) return;
        saveList(listName.trim(), editingListId || undefined);
        setListName("");
        setEditingListId(null);
        setSaveDialogOpen(false);
    };

    const handleLoadList = (list: SavedShoppingList) => {
        loadList(list);
    };

    const handleDeleteList = (listId: string) => {
        deleteList(listId);
    };

    const handleEditList = (list: SavedShoppingList) => {
        const editInfo = editList(list);
        setListName(editInfo.name);
        setEditingListId(editInfo.id);
        setSaveDialogOpen(true);
    };

    const openSaveDialog = () => {
        setListName("");
        setEditingListId(null);
        setSaveDialogOpen(true);
    };

        // Barcode scanning functions
    const handleBarcodeScan = () => {
        setShowCameraScanner(true);
    };

    const handleCameraScan = (barcode: string) => {
        setShowCameraScanner(false);
        
        const foundProduct = products.find(product => 
            product.EAN === barcode || 
            product.unique_id === barcode
        );
        
        if (foundProduct) {
            setScannedProduct(foundProduct);
            setBarcodeDialogOpen(true);
        } else {
            alert(`Product with barcode "${barcode}" not found. Please try again.`);
        }
    };

    const handleCloseCameraScanner = () => {
        setShowCameraScanner(false);
    };

    // Helper: find currently active backend shopping list (if any)
    const getActiveListId = (): string | null => {
        const active = savedLists.find((l) => l.active);
        if (active) return active.id;
        // Fallback to persisted active list id, in case this page loaded before
        // Redux state was fully hydrated with active flags.
        const stored = localStorage.getItem("activeShoppingListId");
        return stored || null;
    };

    const syncQuantityWithBackend = async (
        productId: string,
        prevQuantity: number,
        newQuantity: number
    ) => {
        const activeListId = getActiveListId();
        if (!activeListId) return;
        try {
            if (newQuantity > prevQuantity) {
                // For increments, use POST /shopping-list/{id}/items to add the delta.
                const delta = newQuantity - prevQuantity;
                await shoppingListApi.addShoppingListItem(activeListId, productId, {
                    product_id: productId,
                    quantity: delta,
                });
                void shoppingListApi.auditEvent(activeListId, "add-item-to-shopping-list", {
                    product_id: productId,
                    delta,
                    prev_quantity: prevQuantity,
                    new_quantity: newQuantity,
                    source: "cart",
                });
            } else if (newQuantity > 0 && newQuantity < prevQuantity) {
                // For decrements that still leave some quantity, patch the remaining quantity.
                await shoppingListApi.updateItemQuantity(activeListId, productId, newQuantity);
                void shoppingListApi.auditEvent(activeListId, "update-item-quantity", {
                    product_id: productId,
                    prev_quantity: prevQuantity,
                    new_quantity: newQuantity,
                    source: "cart",
                });
            } else if (newQuantity <= 0) {
                // When quantity hits zero, remove the product entirely from the list.
                await shoppingListApi.deleteShoppingListItem(activeListId, productId);
                void shoppingListApi.auditEvent(activeListId, "remove-item-from-shopping-list", {
                    product_id: productId,
                    prev_quantity: prevQuantity,
                    new_quantity: newQuantity,
                    source: "cart",
                });
            }
        } catch (error) {
            console.error("Failed to sync shopping list quantity with backend:", error);
        }
    };

    const syncRemoveProductCompletely = async (productId: string) => {
        const activeListId = getActiveListId();
        if (!activeListId) return;
        try {
            await shoppingListApi.deleteShoppingListItem(activeListId, productId);
            void shoppingListApi.auditEvent(activeListId, "remove-item-from-shopping-list", {
                product_id: productId,
                source: "cart-drawer",
            });
        } catch (error) {
            console.error("Failed to remove product from backend shopping list:", error);
        }
    };

    const syncClearListWithBackend = async () => {
        const activeListId = getActiveListId();
        if (!activeListId) return;
        try {
            await shoppingListApi.deleteAllShoppingListItems(activeListId);
            void shoppingListApi.auditEvent(activeListId, "clear-shopping-list", {
                source: "cart-drawer",
            });
        } catch (error) {
            console.error("Failed to clear backend shopping list:", error);
        }
    };

    const handleAddScannedProduct = async () => {
        if (scannedProduct) {
            const productId = scannedProduct.unique_id;
            const prevQty = shoppingList[productId] || 0;
            const newQty = prevQty + 1;
            addItem(productId);
            await syncQuantityWithBackend(productId, prevQty, newQty);
            setBarcodeDialogOpen(false);
            setScannedProduct(null);
        }
    };

    const handleCloseBarcodeDialog = () => {
        setBarcodeDialogOpen(false);
        setScannedProduct(null);
    };

    const handleCloseWelcomeDialog = () => {
        setWelcomeDialogOpen(false);
        localStorage.setItem('hasSeenWelcome', 'true');
    };

    const handleSeeStoreMap = () => {
        setWelcomeDialogOpen(false);
        localStorage.setItem('hasSeenWelcome', 'true');
        navigate('/map');
    };



    return (
        <ProductsLoader>
            <MobileContainer
                footer={
                    <ShoppingListDrawer
                        selectedProducts={selectedProducts}
                        totalPrice={totalPrice}
                        totalDiscount={totalDiscount}
                        onRemoveItem={async (id) => {
                            const productId = id.toString();
                            removeItemFromList(productId);
                            await syncRemoveProductCompletely(productId);
                        }}
                        onSaveList={openSaveDialog}
                        onLoadList={handleLoadList}
                        onEditList={handleEditList}
                        onDeleteList={handleDeleteList}
                        savedLists={savedLists}
                        canSave={Object.keys(shoppingList).length > 0}
                    />
                }
            >
                <Box sx={{ pb: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                            {showFavorites ? "Favorites" : "Shopping Cart"}
                        </Typography>
                        <Tooltip title="Scan barcode">
                            <IconButton size="small" onClick={handleBarcodeScan}>
                                <CameraAltIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton 
                            size="small" 
                            onClick={toggleFavoritesView}
                            sx={{ 
                                color: showFavorites ? MAIN_COLOR_RED : 'inherit',
                                backgroundColor: showFavorites ? 'rgba(220, 38, 38, 0.1)' : 'transparent'
                            }}
                        >
                            <FavoriteIcon />
                        </IconButton>
                        {Object.keys(shoppingList).length > 0 && (
                            <IconButton 
                                size="small" 
                                onClick={async () => {
                                    clearCurrentList();
                                    await syncClearListWithBackend();
                                }}
                                sx={{ 
                                    color: 'error.main',
                                    "&:hover": {
                                        backgroundColor: "rgba(244, 67, 54, 0.1)",
                                    }
                                }}
                                title="Clear shopping cart"
                            >
                                <RemoveShoppingCartIcon />
                            </IconButton>
                        )}
                    </Box>
                </Box>
                <RoundedSearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search products..."
                />

                {/* Category Filter */}
                <Box sx={{ mt: 2, mb: 2, overflowX: "auto" }}>
                    <Box sx={{ display: "flex", gap: 1, minWidth: "max-content" }}>
                <Chip
                    label="All"
                    onClick={() => setSelectedCategory(null)}
                            variant={selectedCategory === null ? "filled" : "outlined"}
                            sx={{ backgroundColor: selectedCategory === null ? MAIN_COLOR_RED : "transparent" }}
                        />
                        {categories.map((category) => (
                    <Chip
                        key={category}
                        label={category}
                        onClick={() => setSelectedCategory(category)}
                                variant={selectedCategory === category ? "filled" : "outlined"}
                                sx={{ backgroundColor: selectedCategory === category ? MAIN_COLOR_RED : "transparent" }}
                    />
                ))}
                    </Box>
            </Box>

                {/* Products Grid */}
            <Grid container spacing={2}>
                    {currentProducts.map((product: RawProduct) => (
                        <Grid item xs={6} key={product.unique_id}>
                            <Card sx={{ 
                                height: "100%", 
                                position: "relative",
                                overflow: "visible"
                            }}>
                                <IconButton
                                    size="small"
                                    onClick={() => toggleFavorite(product.unique_id)}
                                    sx={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                                        zIndex: 1,
                                        "&:hover": {
                                            backgroundColor: "rgba(255, 255, 255, 1)",
                                        },
                                    }}
                                >
                                    {favorites.has(product.unique_id) ? (
                                        <FavoriteIcon sx={{ color: MAIN_COLOR_RED }} />
                                    ) : (
                                        <FavoriteBorderIcon />
                                    )}
                                </IconButton>
                            <CardMedia
                                component="img"
                                    height="140"
                                    image={product.image_url}
                                alt={product.title}
                                    sx={{ objectFit: "cover" }}
                            />
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="body2" fontWeight="bold" noWrap>
                                    {product.title}
                                </Typography>
                                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                        {product.brand}
                                </Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                                            <Box>
                                                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                                    <Typography variant="h6" fontWeight="bold" color={MAIN_COLOR_RED}>
                                                        €{parseFloat(product.sale_price).toFixed(2)}
                                </Typography>
                                                    {product.original_sales_unit && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            /{product.original_sales_unit.replace('unit', '').trim()}
                                        </Typography>
                                                    )}
                                                </Box>
                                                {product.barred_price && parseFloat(product.barred_price) > parseFloat(product.sale_price) && (
                                                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                textDecoration: "line-through", 
                                                                color: "text.secondary"
                                                            }}
                                                        >
                                                            €{parseFloat(product.barred_price).toFixed(2)}
                                                        </Typography>
                                                        {product.original_sales_unit && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                                                                /{product.original_sales_unit.replace('unit', '').trim()}
                                    </Typography>
                                )}
                                                    </Box>
                                                )}
                                            </Box>
                                            {product.promo_percentage && parseFloat(product.promo_percentage) > 0 && (
                                                <Chip
                                                    label={`${product.promo_percentage}% OFF`}
                                                    size="small"
                                                    sx={{ backgroundColor: MAIN_COLOR_RED, color: "white" }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                            </CardContent>
                                <CardActions sx={{ 
                                    p: 1.5, 
                                    pt: 0,
                                    minHeight: "60px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: product.unique_id in shoppingList ? "center" : "flex-end"
                                }}>
                                    {product.unique_id in shoppingList ? (
                                        <Box sx={{ 
                                            display: "flex", 
                                            alignItems: "center", 
                                            gap: 1, 
                                            width: "100%",
                                            minHeight: "40px",
                                            touchAction: "manipulation",
                                            position: "relative",
                                            zIndex: 5
                                        }}>
                                            <IconButton
                                                size="small"
                                                onClick={async () => {
                                                    const productId = product.unique_id;
                                                    const prevQty = shoppingList[productId] || 0;
                                                    if (prevQty <= 0) return;
                                                    const newQty = prevQty - 1;
                                                    removeItem(productId);
                                                    await syncQuantityWithBackend(productId, prevQty, newQty);
                                                }}
                                                sx={{ 
                                                    backgroundColor: MAIN_COLOR_RED, 
                                                    color: "white",
                                                    minWidth: "40px",
                                                    height: "40px",
                                                    touchAction: "manipulation",
                                                    WebkitTapHighlightColor: "transparent",
                                                    zIndex: 10,
                                                    position: "relative",
                                                    "&:hover": {
                                                        backgroundColor: MAIN_COLOR_RED,
                                                    },
                                                    "&:active": {
                                                        backgroundColor: MAIN_COLOR_RED,
                                                    }
                                                }}
                                            >
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>
                                            <Typography variant="body2" sx={{ flex: 1, textAlign: "center" }}>
                                                {shoppingList[product.unique_id]}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={async () => {
                                                    const productId = product.unique_id;
                                                    const prevQty = shoppingList[productId] || 0;
                                                    const newQty = prevQty + 1;
                                                    addItem(productId);
                                                    await syncQuantityWithBackend(productId, prevQty, newQty);
                                                }}
                                                sx={{ 
                                                    backgroundColor: MAIN_COLOR_RED, 
                                                    color: "white",
                                                    minWidth: "40px",
                                                    height: "40px",
                                                    touchAction: "manipulation",
                                                    WebkitTapHighlightColor: "transparent",
                                                    zIndex: 10,
                                                    position: "relative",
                                                    "&:hover": {
                                                        backgroundColor: MAIN_COLOR_RED,
                                                    },
                                                    "&:active": {
                                                        backgroundColor: MAIN_COLOR_RED,
                                                    }
                                                }}
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <IconButton
                                            size="small"
                                            onClick={async () => {
                                                const productId = product.unique_id;
                                                const prevQty = shoppingList[productId] || 0;
                                                const newQty = prevQty + 1;
                                                addItem(productId);
                                                await syncQuantityWithBackend(productId, prevQty, newQty);
                                            }}
                                            sx={{ 
                                                backgroundColor: MAIN_COLOR_RED, 
                                                color: "white",
                                                minWidth: "40px",
                                                width: "40px",
                                                height: "40px",
                                                touchAction: "manipulation",
                                                WebkitTapHighlightColor: "transparent",
                                                '&:hover': {
                                                    backgroundColor: MAIN_COLOR_RED,
                                                },
                                                '&:active': {
                                                    backgroundColor: MAIN_COLOR_RED,
                                                }
                                            }}
                                >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    )}
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: 1, 
                    mt: 3, 
                    mb: 4,
                    pb: 2,
                    position: 'relative',
                    zIndex: 1
                }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        sx={{ 
                            borderColor: MAIN_COLOR_RED, 
                            color: MAIN_COLOR_RED,
                            '&:hover': {
                                borderColor: MAIN_COLOR_RED,
                                backgroundColor: 'rgba(220, 38, 38, 0.04)',
                            }
                        }}
                    >
                        Previous
                    </Button>
                    
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                                <Button
                                    key={pageNum}
                                    variant={currentPage === pageNum ? "contained" : "outlined"}
                                    size="small"
                                    onClick={() => setCurrentPage(pageNum as number)}
                                    sx={{ 
                                        minWidth: '32px',
                                        height: '32px',
                                        backgroundColor: currentPage === pageNum ? MAIN_COLOR_RED : 'transparent',
                                        borderColor: MAIN_COLOR_RED,
                                        color: currentPage === pageNum ? 'white' : MAIN_COLOR_RED,
                                        '&:hover': {
                                            backgroundColor: currentPage === pageNum ? MAIN_COLOR_RED : 'rgba(220, 38, 38, 0.04)',
                                        }
                                    }}
                                >
                                    {pageNum}
                                </Button>
                            );
                        })}
                    </Box>
                    
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        sx={{ 
                            borderColor: MAIN_COLOR_RED, 
                            color: MAIN_COLOR_RED,
                            '&:hover': {
                                borderColor: MAIN_COLOR_RED,
                                backgroundColor: 'rgba(220, 38, 38, 0.04)',
                            }
                        }}
                    >
                        Next
                    </Button>
                </Box>
            )}

            {/* Products Info */}
            <Box sx={{ 
                textAlign: 'center', 
                mt: 1, 
                mb: 4,
                pb: 2,
                color: 'text.secondary',
                fontSize: '0.875rem',
                position: 'relative',
                zIndex: 1
            }}>
                Showing {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} products
            </Box>
                </Box>
        </MobileContainer>

        {/* Save List Dialog */}
        <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
                {editingListId ? "Edit Shopping List" : "Save Shopping List"}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="List Name"
                    fullWidth
                    variant="outlined"
                    value={listName}
                    onChange={(e) => setListName(e.target.value)}
                    placeholder="Enter a name for your shopping list"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
                <Button 
                    onClick={handleSaveList} 
                    variant="contained"
                    disabled={!listName.trim()}
                    sx={{ backgroundColor: MAIN_COLOR_RED }}
                >
                    {editingListId ? "Update" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Barcode Scan Dialog */}
        <Dialog open={barcodeDialogOpen} onClose={handleCloseBarcodeDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
                Product Found!
            </DialogTitle>
            <DialogContent>
                {scannedProduct && (
                    <Box>
                        <ListItem sx={{ px: 0 }}>
                            <ListItemAvatar>
                                <Avatar
                                    variant="square"
                                    src={scannedProduct.image_url}
                                    alt={scannedProduct.title}
                                    sx={{ width: 60, height: 60 }}
                                />
                            </ListItemAvatar>
                            <ListItemText
                                primary={scannedProduct.title}
                                secondary={
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {scannedProduct.brand}
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 0.5 }}>
                                            <Typography variant="h6" fontWeight="bold" color={MAIN_COLOR_RED}>
                                                €{parseFloat(scannedProduct.sale_price).toFixed(2)}
                                            </Typography>
                                            {scannedProduct.original_sales_unit && (
                                                <Typography variant="caption" color="text.secondary">
                                                    /{scannedProduct.original_sales_unit.replace('unit', '').trim()}
                                                </Typography>
                                            )}
                                        </Box>
                                        {scannedProduct.barred_price && parseFloat(scannedProduct.barred_price) > parseFloat(scannedProduct.sale_price) && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    textDecoration: "line-through",
                                                    color: "text.secondary"
                                                }}
                                            >
                                                €{parseFloat(scannedProduct.barred_price).toFixed(2)}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                        </ListItem>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Add this product to your shopping list?
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseBarcodeDialog}>Cancel</Button>
                <Button 
                    onClick={handleAddScannedProduct} 
                    variant="contained"
                    sx={{ backgroundColor: MAIN_COLOR_RED }}
                >
                    Add to Cart
                </Button>
            </DialogActions>
        </Dialog>

        {/* Camera Barcode Scanner */}
        {showCameraScanner && (
            <BarcodeScanner
                onScan={handleCameraScan}
                onClose={handleCloseCameraScanner}
            />
        )}

        {/* Welcome Dialog for First-Time Users */}
        <Dialog 
            open={welcomeDialogOpen} 
            onClose={handleCloseWelcomeDialog}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    padding: '24px',
                }
            }}
        >
            <DialogContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                textAlign: 'center',
                pt: 4,
                pb: 2
            }}>
                <LocationOnIcon 
                    sx={{ 
                        fontSize: 80, 
                        color: 'text.primary',
                        mb: 3
                    }} 
                />
                <Typography 
                    variant="h5" 
                    fontWeight="bold" 
                    sx={{ mb: 2 }}
                >
                    Welcome to Loja de Matosinhos
                </Typography>
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    On this continent you can pay with Continente Pay for greater convenience
                </Typography>
            </DialogContent>
            <DialogActions sx={{ 
                flexDirection: 'column', 
                gap: 1, 
                px: 3, 
                pb: 3,
                '& > *': {
                    width: '100%',
                    margin: 0
                }
            }}>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleCloseWelcomeDialog}
                    sx={{ 
                        backgroundColor: MAIN_COLOR_RED,
                        borderRadius: '8px',
                        py: 1.5,
                        '&:hover': {
                            backgroundColor: MAIN_COLOR_RED,
                        }
                    }}
                >
                    Continue
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleSeeStoreMap}
                    sx={{ 
                        borderColor: MAIN_COLOR_RED,
                        color: MAIN_COLOR_RED,
                        borderRadius: '8px',
                        py: 1.5,
                        '&:hover': {
                            borderColor: MAIN_COLOR_RED,
                            backgroundColor: 'rgba(220, 38, 38, 0.04)',
                        }
                    }}
                >
                    See store map
                </Button>
            </DialogActions>
        </Dialog>

        </ProductsLoader>
    );
};

export default ShoppingCartPage;
